import {
  Link as _Link,
  Navigate as _Navigate,
  useNavigate as _useNavigate,
  type LinkProps,
  type NavigateOptions,
  type NavigateProps,
  useParams,
  // biome-ignore lint/style/noRestrictedImports: this module is the wrapper around the Modern.js router
} from '@modern-js/runtime/router';

import type { TRoutes } from './types';

/* type TRelativePats = {
  '/org/[slug]/profile/':
    | 'documents'
    | 'e-learning'
    | 'free-time'
    | 'information'
    | 'learning'
    | 'performance';
};

const navigateRelativeTo = (parentRoute: '/org/[slug]/profile/', ) */

export {
  createBrowserRouter,
  isRouteErrorResponse,
  type LinkProps,
  MemoryRouter,
  Outlet,
  Route,
  type RouteObject,
  RouterProvider,
  Routes,
  useActionData,
  useAsyncError,
  useAsyncValue,
  useLoaderData,
  useLocation,
  useMatches,
  useOutlet,
  useParams,
  useRouteError,
  useRouteLoaderData,
  useSearchParams,
  // biome-ignore lint/style/noRestrictedImports: this module is the wrapper around the Modern.js router
} from '@modern-js/runtime/router';

// TODO: make sure this is working as expected
const buildUrl = ({
  path,
  params,
  searchParams,
}: {
  path: string;
  params?: Record<string, string | number>;
  searchParams?: Record<string, string | number>;
}) => {
  // add search params
  const parsedSearchParams = Object.entries(searchParams || {})
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

  if (params === undefined) {
    return `${path}${searchParams ? `?${parsedSearchParams}` : ''}`;
  }

  // apply params
  const entries = Object.entries(params);
  let builtPath = path;

  entries.forEach(([key, value]) => {
    builtPath = builtPath.replace(`[${key}]`, String(value));
  });

  return `${builtPath}${searchParams ? `?${parsedSearchParams}` : ''}`;
};

export type TPathsWithoutParams = TRoutes extends infer T
  ? T extends { path: string; params: undefined }
    ? T['path']
    : never
  : never;

type TPathObjectsNormalized = TRoutes extends infer T
  ? T extends { params: undefined }
    ? Omit<T, 'params'>
    : T
  : never;

export type TNavigationData = TPathsWithoutParams | TPathObjectsNormalized;

type TLinkProps = Omit<LinkProps, 'to'> & {
  to: TNavigationData;
};

/**
 * Our custom Link component with enhanced functionality.
 * The `to` prop can be a string or an object with the following properties:
 * - path: The path to navigate to
 * - params?: The params to replace in the path
 * - searchParams?: The search params to replace in the path
 * How to use?:
 * ```
 * <Link to="/org/[slug]/admin/">
 *  Go to path
 * </Link>
 * ````
 * Keep in mind that the [slug] param is automatically replaced with the current slug of the current url
 */
export function Link({ to, ...props }: TLinkProps) {
  const { slug = '' } = useParams<{ slug: string }>();

  const href =
    typeof to === 'string'
      ? to.replace('[slug]', slug)
      : buildUrl({
          ...to,
          path: to.path.replace('[slug]', slug),
        });

  return <_Link to={href} {...props} />;
}

type TNavigateProps = Omit<NavigateProps, 'to'> & {
  to: TNavigationData;
};

export function Navigate(props: TNavigateProps) {
  const { slug = '' } = useParams<{ slug: string }>();

  const to =
    typeof props.to === 'string'
      ? props.to.replace('[slug]', slug)
      : buildUrl({
          ...props.to,
          path: props.to.path.replace('[slug]', slug),
        });

  return <_Navigate {...props} to={to} />;
}

/**
 * A custom hook to navigate with enhanced functionality.
 * This hook returns:
 * - navigateTo: A function to navigate to a path with or without params and searchParams
 * - navigateToLogin: A function to navigate to the login page
 * - createUrl: A function to create a url with or without params and searchParams
 * The navigateTo function uses the original @modernjs/runtime/router navigate function but it will give your autocomplete suggestions to use the correct path
 */
export const useNavigate = () => {
  const _navigate = _useNavigate();
  const { slug = '' } = useParams<{ slug: string }>();

  const navigateToLogin = () => {
    const next = `${window.location.pathname}${window.location.search}`;
    window.location.href = `/org/${slug}/entrar/?next=${next}`;
  };

  const createUrl = (params: TNavigationData) => {
    if (typeof params === 'string') {
      return params.replace('[slug]', slug);
    }

    return buildUrl({
      ...params,
      path: params.path.replace('[slug]', slug),
    });
  };

  const navigateTo = (params: TNavigationData, options?: NavigateOptions) => {
    const finalPath = createUrl(params);
    _navigate(finalPath, options);
  };

  const goBack = () => {
    _navigate(-1);
  };

  return {
    navigateToLogin,
    /**
     * Original navigate function returned by the original modern-router useNavigate
     */
    _originalNavigate: _navigate,
    navigateTo,
    createUrl,
    goBack,
  };
};
